// components/WorkPlan.jsx - Work plan with crew assignments
import React from 'react';

const WorkPlan = ({ data, onChange, crewMembers = [], jobInfo = {} }) => {
  // Initialize data structure with default neighbor permission
  const workPlan = data || {
    climbingDifficulty: jobInfo.customFields?.climbingDifficulty || null,
    riggingDifficulty: jobInfo.customFields?.riggingDifficulty || null,
    workZoneSecurity: '',
    neighborPermission: 'no', // Default to "No access needed"
    neighborNotes: '',
    crewAssignments: {
      groundControl: '',
      call911: '',
      aerialRescue: ''
    }
  };

  // Update parent
  const updateWorkPlan = (updates) => {
    onChange({ ...workPlan, ...updates });
  };

  // Difficulty rating component
  const DifficultyRating = ({ label, value, field, description }) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        <span style={{ fontWeight: '500' }}>{label}</span>
        <span style={{ 
          fontSize: '24px', 
          fontWeight: '700',
          color: value ? getDifficultyColor(value) : '#9ca3af'
        }}>
          {value || '—'}/5
        </span>
      </div>
      
      <div style={{ display: 'flex', gap: '8px' }}>
        {[1, 2, 3, 4, 5].map(num => (
          <button
            key={num}
            onClick={() => updateWorkPlan({ [field]: num })}
            style={{
              flex: 1,
              padding: '12px 8px',
              backgroundColor: value === num ? getDifficultyColor(num) : '#F1F0E9',
              color: value === num ? 'white' : '#233D48',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: value === num ? '600' : '400',
              fontSize: '16px'
            }}
          >
            {num}
          </button>
        ))}
      </div>
      
      {description && (
        <p style={{ fontSize: '12px', color: '#5D757E', marginTop: '4px' }}>
          {description}
        </p>
      )}
    </div>
  );

  // Get color based on difficulty
  function getDifficultyColor(value) {
    if (value <= 2) return '#388523'; // Green - Easy
    if (value === 3) return '#CDB52D'; // Yellow - Moderate
    if (value === 4) return '#B36109'; // Orange - Hard
    return '#D24232'; // Red - Very Hard
  }

  // Update Crew Assignments
  const updateCrewAssignment = (role, memberId) => {
    updateWorkPlan({
      crewAssignments: {
        ...workPlan.crewAssignments,
        [role]: memberId
      }
    });
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <h2 style={{ 
        fontSize: '18px', 
        fontWeight: '600', 
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        📋 Work Plan
      </h2>

      {/* Work Zone Security Plan - REQUIRED */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        border: '1px solid #DADFE2'
      }}>
        <label style={{ fontWeight: '500', display: 'block', marginBottom: '8px' }}>
          How will the work zone be secured?
          <span style={{ color: '#D24232', marginLeft: '4px' }}>*</span>
        </label>
        <textarea
          value={workPlan.workZoneSecurity || ''}
          onChange={(e) => updateWorkPlan({ workZoneSecurity: e.target.value })}
          placeholder="Describe plan to ensure no unwelcome visitors will enter the work zone (cones, signs, barriers, flaggers, or other work zone controls)..."
          rows={3}
          style={{
            width: '100%',
            padding: '12px',
            paddingLeft: '16px',
            border: '1px solid #DADFE2',
            borderLeft: `4px solid ${workPlan.workZoneSecurity?.trim() ? '#388523' : '#D24232'}`,
            borderRadius: '6px',
            fontSize: '15px',
            resize: 'vertical',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* DIFFICULTY RATINGS */}
      <div style={{
        backgroundColor: '#F1F0E9',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#032B3A' }}>
          Job Difficulty Assessment
        </h3>

        <DifficultyRating
          label="Climbing Difficulty"
          value={workPlan.climbingDifficulty}
          field="climbingDifficulty"
          description="1 = Easy access, 5 = Technical climb required"
        />

        <DifficultyRating
          label="Rigging Difficulty"
          value={workPlan.riggingDifficulty}
          field="riggingDifficulty"
          description="1 = Simple drops, 5 = Complex technical rigging"
        />

        {/* Overall difficulty indicator - COMMENTED OUT */}
        {/* {workPlan.climbingDifficulty && workPlan.riggingDifficulty && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: getDifficultyColor(
              Math.max(workPlan.climbingDifficulty, workPlan.riggingDifficulty)
            ),
            borderRadius: '8px',
            color: 'white',
            textAlign: 'center'
          }}>
            <span style={{ fontWeight: '600' }}>
              {Math.max(workPlan.climbingDifficulty, workPlan.riggingDifficulty) >= 4
                ? '⚠️ HIGH DIFFICULTY JOB - Extra caution required'
                : Math.max(workPlan.climbingDifficulty, workPlan.riggingDifficulty) >= 3
                ? '⚡ MODERATE DIFFICULTY - Stay alert'
                : '✓ STANDARD DIFFICULTY'}
            </span>
          </div>
        )} */}
      </div>

      {/* Neighbor Permission */}
      <div style={{
        backgroundColor: '#F1F0E9',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <label style={{ fontWeight: '500', display: 'block', marginBottom: '12px' }}>
          Has neighbor given permission to access yard?
        </label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          {[
            { value: 'yes', label: 'Yes - Permission Obtained', color: '#388523' },
            { value: 'no', label: 'No - Access Not Needed', color: '#5D757E' },
            { value: 'pending', label: 'Pending', color: '#CDB52D' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => updateWorkPlan({ neighborPermission: option.value })}
              style={{
                flex: 1,
                padding: '12px 8px',
                backgroundColor: workPlan.neighborPermission === option.value ? option.color : 'white',
                color: workPlan.neighborPermission === option.value ? 'white' : '#233D48',
                border: workPlan.neighborPermission === option.value ? 'none' : '1px solid #DADFE2',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: workPlan.neighborPermission === option.value ? '600' : '400'
              }}
            >
              {option.label}
            </button>
          ))}
        </div>

        {workPlan.neighborPermission === 'yes' && (
          <input
            type="text"
            value={workPlan.neighborNotes || ''}
            onChange={(e) => updateWorkPlan({ neighborNotes: e.target.value })}
            placeholder="Neighbor's name / notes..."
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #DADFE2',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        )}
      </div>

      {/* Crew Assignments - Ground Control and Call 911 REQUIRED */}
      {crewMembers.length > 0 && (
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #DADFE2'
        }}>
          <label style={{ fontWeight: '500', display: 'block', marginBottom: '12px' }}>
            Crew Member Assignments
            <span style={{ color: '#D24232', marginLeft: '4px', fontSize: '12px' }}>
              (Ground Control and Call 911 required)
            </span>
          </label>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Ground Control - REQUIRED */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500',
                marginBottom: '6px',
                color: '#032B3A'
              }}>
                Ground Control
                <span style={{ color: '#D24232', marginLeft: '4px' }}>*</span>
              </label>
              <select
                value={workPlan.crewAssignments?.groundControl || ''}
                onChange={(e) => updateCrewAssignment('groundControl', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  paddingLeft: '16px',
                  border: '1px solid #DADFE2',
                  borderLeft: `4px solid ${workPlan.crewAssignments?.groundControl ? '#388523' : '#D24232'}`,
                  borderRadius: '6px',
                  fontSize: '15px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select crew member...</option>
                {crewMembers.map((member, index) => (
                  <option key={index} value={member.name}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Call 911 - REQUIRED */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500',
                marginBottom: '6px',
                color: '#032B3A'
              }}>
                Call 911
                <span style={{ color: '#D24232', marginLeft: '4px' }}>*</span>
              </label>
              <select
                value={workPlan.crewAssignments?.call911 || ''}
                onChange={(e) => updateCrewAssignment('call911', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  paddingLeft: '16px',
                  border: '1px solid #DADFE2',
                  borderLeft: `4px solid ${workPlan.crewAssignments?.call911 ? '#388523' : '#D24232'}`,
                  borderRadius: '6px',
                  fontSize: '15px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select crew member...</option>
                {crewMembers.map((member, index) => (
                  <option key={index} value={member.name}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Aerial Rescue (AR) - OPTIONAL */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500',
                marginBottom: '6px',
                color: '#032B3A'
              }}>
                Aerial Rescue (AR)
                <span style={{ fontSize: '12px', color: '#5D757E', fontWeight: '400', marginLeft: '8px' }}>
                  (optional)
                </span>
              </label>
              <select
                value={workPlan.crewAssignments?.aerialRescue || ''}
                onChange={(e) => updateCrewAssignment('aerialRescue', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #DADFE2',
                  borderRadius: '6px',
                  fontSize: '15px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select crew member (optional)...</option>
                {crewMembers.map((member, index) => (
                  <option key={index} value={member.name}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkPlan;